// src/pages/admin/UtilizadoresFuncoes.js
import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import createAutocompleteFilter from "pages/utils/autocompleteFilter";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const autocompleteFilter = createAutocompleteFilter();

export default function UtilizadoresFuncoes() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [data, setData] = useState([]);
  const [utilizadores, setUtilizadores] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [distribuicaoNomes, setDistribuicaoNomes] = useState([]);

  const carregar = async () => {
    const [rows, err] = await handleApi(api.post("UtilizadoresFuncoes/SelUtilizadoresFuncoes", {}));
    if (err) return showAlert(err, "error");
    setData(Array.isArray(rows) ? rows : []);
  };

  const carregarCombos = async () => {
    const [u, errU] = await handleApi(api.post("UtilizadoresFuncoes/SelUtilizadoresCombo", {}));
    if (errU) return showAlert(errU, "error");
    const [f, errF] = await handleApi(api.post("UtilizadoresFuncoes/SelFuncoesCombo", {}));
    if (errF) return showAlert(errF, "error");
    setUtilizadores(Array.isArray(u) ? u : []);
    setFuncoes(Array.isArray(f) ? f : []);
  };

  const carregarDistribuicaoNomes = async (idutilizador = null) => {
    const [rows, err] = await handleApi(
      api.post("UtilizadoresFuncoes/SelDistribuicaoNomesDisponiveis", {
        idutilizador: idutilizador ? Number(idutilizador) : null,
      })
    );

    if (err) {
      showAlert(err, "error");
      setDistribuicaoNomes([]);
      return;
    }

    setDistribuicaoNomes(Array.isArray(rows) ? rows : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
      await carregarCombos();
    })();
  }, []);

  // ---- dialog inserir
  const [openAdd, setOpenAdd] = useState(false);
  const [novo, setNovo] = useState({
    idutilizador: "",
    idfuncao: "",
    distribuicaonome: "",
  });

  // ---- dialog editar
  const [openEdit, setOpenEdit] = useState(false);
  const [edicao, setEdicao] = useState(null);

  const funcaoDistribuicaoSelecionada =
    funcoes.find((f) => String(f.id) === String(novo.idfuncao))?.designacao === "Distribuição";

  const funcaoDistribuicaoEdicao =
    funcoes.find((f) => String(f.id) === String(edicao?.idfuncao))?.designacao === "Distribuição";

  const inserir = async () => {
    if (!novo.idutilizador || !novo.idfuncao) {
      showAlert("Seleciona Utilizador e Função.", "warning");
      return;
    }

    const body = {
      idutilizador: Number(novo.idutilizador),
      idfuncao: Number(novo.idfuncao),
      distribuicaonome: novo.distribuicaonome || null,
    };
    const [_, err, meta] = await handleApi(
      api.post("UtilizadoresFuncoes/InsUtilizadorFuncao", body)
    );

    if (err) return showAlert(err, "error");

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }

    await carregar();

    setOpenAdd(false);

    setNovo({
      idutilizador: "",
      idfuncao: "",
      distribuicaonome: "",
    });

    setDistribuicaoNomes([]);
  };

  const gravarEdicao = async () => {
    if (!edicao?.id || !edicao?.idutilizador || !edicao?.idfuncao) {
      showAlert("Seleciona Utilizador e Função.", "warning");
      return;
    }

    const body = {
      id: Number(edicao.id),
      idutilizador: Number(edicao.idutilizador),
      idfuncao: Number(edicao.idfuncao),
      distribuicaonome: edicao.distribuicaonome || null,
    };

    const [_, err, meta] = await handleApi(
      api.post("UtilizadoresFuncoes/UpdUtilizadorFuncao", body)
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }

    await carregar();

    setOpenEdit(false);
    setEdicao(null);
    setDistribuicaoNomes([]);
  };

  // ---- confirmação eliminar
  const [openConfirm, setOpenConfirm] = useState(false);
  const [alvo, setAlvo] = useState(null);

  const pedirConfirmacao = (row) => {
    setAlvo(row);
    setOpenConfirm(true);
  };
  const fecharConfirmacao = () => {
    setOpenConfirm(false);
    setAlvo(null);
  };

  const confirmarEliminar = async () => {
    if (!alvo?.id) return fecharConfirmacao();
    const [_, err, meta] = await handleApi(
      api.post("UtilizadoresFuncoes/DelUtilizadorFuncao", { id: alvo.id })
    );
    if (err) showAlert(err, "error");
    else if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    fecharConfirmacao();
  };

  const abrirEdicao = async (a) => {
    const isDistribuicao = a.funcao === "Distribuição";

    setEdicao({
      id: a.id,
      idutilizador: a.idutilizador,
      idfuncao: a.idfuncao,
      utilizador: a.utilizador,
      funcao: a.funcao,
      distribuicaonome: a.distribuicaonome || "",
    });

    setOpenEdit(true);

    if (isDistribuicao) {
      await carregarDistribuicaoNomes(a.idutilizador);
    } else {
      setDistribuicaoNomes([]);
    }
  };

  const projectColumns = useMemo(
    () => [
      {
        Header: "Utilizador",
        accessor: "utilizador",
        width: 320,
        minWidth: 180,
      },
      {
        Header: "Função",
        accessor: "funcao",
        width: 260,
        minWidth: 160,
      },
      {
        Header: "Nome na Distribuição",
        accessor: "distribuicaonome",
        width: 280,
        minWidth: 180,
        Cell: ({ value }) => value || "—",
      },
    ],
    []
  );

  const projectRowActions = useMemo(
    () => [
      {
        id: "editar",
        icon: <EditIcon fontSize="small" />,
        tooltip: "Editar",
        color: "warning",
        visible: () => temPermissao("Menu Administração", "u"),
        onClick: abrirEdicao,
      },
      {
        id: "eliminar",
        icon: <DeleteIcon fontSize="small" />,
        tooltip: "Eliminar",
        color: "error",
        visible: () => temPermissao("Menu Administração", "d"),
        onClick: pedirConfirmacao,
      },
    ],
    [temPermissao]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <ProjectTable
          tableKey="utilizadores-funcoes"
          version="v1"
          columns={projectColumns}
          data={data}
          rowActions={projectRowActions}
          actionsPosition="left"
          minWidth={1000}
          maxHeight="68vh"
          emptyMessage="Sem associações entre utilizadores e funções."
          defaultSortBy={[
            { id: "utilizador", desc: false },
            { id: "funcao", desc: false },
          ]}
          toolbarActions={[
            {
              id: "nova-associacao",
              label: "Nova Associação",
              color: "success",
              disabled: !temPermissao("Menu Administração", "w"),
              onClick: () => setOpenAdd(true),
            },
          ]}
          showKeepFilters
        />
      </MDBox>

      {/* Inserir */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Associação</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Utilizador</div>
              <Autocomplete
                size="small"
                options={utilizadores}
                filterOptions={autocompleteFilter}
                getOptionLabel={(o) => o.designacao || ""}
                value={utilizadores.find((u) => String(u.id) === String(novo.idutilizador)) || null}
                onChange={async (_, v) => {
                  const idutilizador = v?.id || "";

                  setNovo((p) => ({
                    ...p,
                    idutilizador,
                    distribuicaonome: "",
                  }));

                  setDistribuicaoNomes([]);

                  if (funcaoDistribuicaoSelecionada) {
                    await carregarDistribuicaoNomes(idutilizador);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione…"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Função</div>
              <Autocomplete
                size="small"
                options={funcoes}
                filterOptions={autocompleteFilter}
                getOptionLabel={(o) => o.designacao || ""}
                value={funcoes.find((f) => String(f.id) === String(novo.idfuncao)) || null}
                onChange={async (_, v) => {
                  const idfuncao = v?.id || "";
                  const isDistribuicao = v?.designacao === "Distribuição";

                  setNovo((p) => ({
                    ...p,
                    idfuncao,
                    distribuicaonome: isDistribuicao ? p.distribuicaonome : "",
                  }));

                  if (isDistribuicao) {
                    await carregarDistribuicaoNomes(novo.idutilizador);
                  } else {
                    setDistribuicaoNomes([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione…"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </div>

            {funcaoDistribuicaoSelecionada && (
              <div>
                <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                  Nome na Distribuição (Unidis)
                </div>

                <Autocomplete
                  size="small"
                  options={distribuicaoNomes}
                  filterOptions={autocompleteFilter}
                  getOptionLabel={(o) => o.nome || ""}
                  value={distribuicaoNomes.find((x) => x.nome === novo.distribuicaonome) || null}
                  onChange={(_, v) =>
                    setNovo((p) => ({
                      ...p,
                      distribuicaonome: v?.nome || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Sem associação"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                        },
                      }}
                    />
                  )}
                  noOptionsText="Não existem nomes disponíveis"
                />
              </div>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAdd(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={inserir} color="success">
            Inserir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Editar */}
      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setEdicao(null);
          setDistribuicaoNomes([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Associação</DialogTitle>

        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Utilizador</div>
              <Autocomplete
                size="small"
                options={utilizadores}
                filterOptions={autocompleteFilter}
                getOptionLabel={(o) => o.designacao || ""}
                value={
                  utilizadores.find((u) => String(u.id) === String(edicao?.idutilizador)) || null
                }
                onChange={async (_, v) => {
                  const idutilizador = v?.id || "";

                  setEdicao((p) => ({
                    ...p,
                    idutilizador,
                    utilizador: v?.designacao || "",
                    distribuicaonome: "",
                  }));

                  setDistribuicaoNomes([]);

                  if (funcaoDistribuicaoEdicao) {
                    await carregarDistribuicaoNomes(idutilizador);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione…"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Função</div>

              <Autocomplete
                size="small"
                options={funcoes}
                filterOptions={autocompleteFilter}
                getOptionLabel={(o) => o.designacao || ""}
                value={funcoes.find((f) => String(f.id) === String(edicao?.idfuncao)) || null}
                onChange={async (_, v) => {
                  const idfuncao = v?.id || "";
                  const isDistribuicao = v?.designacao === "Distribuição";

                  setEdicao((p) => ({
                    ...p,
                    idfuncao,
                    funcao: v?.designacao || "",
                    distribuicaonome: isDistribuicao ? p.distribuicaonome : "",
                  }));

                  if (isDistribuicao) {
                    await carregarDistribuicaoNomes(edicao?.idutilizador);
                  } else {
                    setDistribuicaoNomes([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione…"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </div>

            {funcaoDistribuicaoEdicao && (
              <div>
                <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                  Nome na Distribuição (Unidis)
                </div>

                <Autocomplete
                  size="small"
                  options={distribuicaoNomes}
                  filterOptions={autocompleteFilter}
                  getOptionLabel={(o) => o.nome || ""}
                  value={distribuicaoNomes.find((x) => x.nome === edicao?.distribuicaonome) || null}
                  onChange={(_, v) =>
                    setEdicao((p) => ({
                      ...p,
                      distribuicaonome: v?.nome || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Sem associação"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                        },
                      }}
                    />
                  )}
                  noOptionsText="Não existem nomes disponíveis"
                />
              </div>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenEdit(false);
              setEdicao(null);
              setDistribuicaoNomes([]);
            }}
            color="secondary"
          >
            Cancelar
          </MDButton>

          <MDButton
            color="success"
            disabled={!temPermissao("Menu Administração", "u")}
            onClick={gravarEdicao}
          >
            Guardar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminação */}
      <Dialog open={openConfirm} onClose={fecharConfirmacao} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          Eliminar a associação <strong>{alvo ? `${alvo.utilizador} — ${alvo.funcao}` : ""}</strong>
          ?
        </DialogContent>
        <DialogActions>
          <MDButton onClick={fecharConfirmacao} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarEliminar} color="error">
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
